import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, MfaRequiredResult } from '@/hooks/useAuth';
import { EnvelopeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import api from '@/services/api';
import toast from 'react-hot-toast';
import TurnstileWidget from '@/components/Common/TurnstileWidget';
import MfaChallengeForm from '@/components/Auth/MfaChallengeForm';

const CAPTCHA_REQUIRED = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // Set once the password (or Google) step comes back asking for a second
  // factor — switches the form over to the code-entry step instead of
  // completing sign-in.
  const [mfaChallenge, setMfaChallenge] = useState<MfaRequiredResult | null>(null);

  const { login, googleSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo as string | undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setUnverifiedEmail(null);

    try {
      const result = await login(email, password, captchaToken || undefined);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        return;
      }
      navigate(returnTo || '/app');
    } catch (error: any) {
      if (error?.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(error.response.data.data?.email || email);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      const result = await googleSignIn(credentialResponse.credential);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        return;
      }
      toast.success('Signed in with Google successfully!');
      navigate(returnTo || '/app');
    } catch (error: any) {
      // Error is handled by the auth hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google sign-in was cancelled or failed');
  };

  // ── MFA code-entry step ────────────────────────────────────────────────
  if (mfaChallenge) {
    return (
      <MfaChallengeForm
        challenge={mfaChallenge}
        onBack={() => setMfaChallenge(null)}
        onSuccess={() => navigate(returnTo || '/app')}
      />
    );
  }

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-8 p-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Sign in to Prime AI
          </h2>
        </div>

        {/* Email not verified banner */}
        {unverifiedEmail && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Email not verified
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                  Please verify <span className="font-medium">{unverifiedEmail}</span> before logging in.
                </p>
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 disabled:opacity-50 transition-colors"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend verification email'}
                </button>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Google Sign-In Button */}
          <div className={`flex justify-center ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="filled_blue"
              text="signin_with"
              shape="rectangular"
            />
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                Or continue with email
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="email" className="sr-only">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Email address"
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Password"
            />
          </div>

          <TurnstileWidget onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} className="flex justify-center" />

          <div>
            <button
              type="submit"
              disabled={isLoading || (CAPTCHA_REQUIRED && !captchaToken)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
          <div className="text-center">
            <Link to="/register" className="text-blue-600 hover:text-blue-500">
              Don't have an account? Sign up
            </Link>
          </div>
        </form>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
